import type { Stop } from '@app/shared/models/tour.type';
import type {
  RawSessionData,
  StopViewEvent,
  AudioPlayEvent,
  ExternalLinkEvent,
  FavoriteEvent,
  ScrollDepthEvent,
  MarkerClickEvent,
} from './types';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { dispatchProfileEvent } from './dispatch-event';
import { isIOS, isAndroid } from '@app/shared/utils/env';

const STORAGE_KEY = 'passive_session_data';

function generateSessionId(): string {
  return Date.now() + '-' + Math.random().toString(36).substring(2, 11);
}

function parseBrowser(): { name: string | null; version: string | null } {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    return { name: 'Chrome', version: ua.match(/Chrome\/([\d.]+)/)?.[1] ?? null };
  }
  if (ua.includes('Safari') && !ua.includes('Chrome')) {
    return { name: 'Safari', version: ua.match(/Version\/([\d.]+)/)?.[1] ?? null };
  }
  if (ua.includes('Firefox')) {
    return { name: 'Firefox', version: ua.match(/Firefox\/([\d.]+)/)?.[1] ?? null };
  }
  if (ua.includes('Edg')) {
    return { name: 'Edge', version: ua.match(/Edg\/([\d.]+)/)?.[1] ?? null };
  }
  return { name: null, version: null };
}

function detectPlatform(): 'web_desktop' | 'web_mobile' {
  const isMobile = isIOS || isAndroid || window.matchMedia('(max-width: 768px)').matches;
  return isMobile ? 'web_mobile' : 'web_desktop';
}

function classifyReferrer(referrer: string): string | null {
  if (!referrer) return 'direct';
  try {
    const host = new URL(referrer).hostname;
    if (host.includes('google')) return 'google_organic';
    if (host.includes('facebook') || host.includes('fb.com')) return 'social_facebook';
    if (host.includes('instagram')) return 'social_instagram';
    if (host.includes('twitter') || host.includes('x.com')) return 'social_twitter';
    if (host === window.location.hostname) return null;
    return 'referral_' + host;
  } catch {
    return 'direct';
  }
}

function extractTags(stop: Stop): string[] {
  return (stop.tags || []).map((t) => t.text).filter(Boolean);
}

/** Haversine distance in meters between two lat/lng points */
function distanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (deg: number) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const RADIUS_METERS = 50;

/** Check if user location is within 50m of a stop */
export function isWithinRadius(
  userLat: number | null,
  userLng: number | null,
  stop: Stop,
): boolean {
  if (userLat == null || userLng == null) return false;
  const loc = stop.stop_location;
  if (!loc?.latitude || !loc?.longitude) return false;
  return distanceMeters(userLat, userLng, loc.latitude, loc.longitude) <= RADIUS_METERS;
}

function createFreshSession(): RawSessionData {
  const browser = parseBrowser();
  const params = new URLSearchParams(window.location.search);

  return {
    session_id: generateSessionId(),
    session_start: Date.now(),
    session_end: 0,
    platform: detectPlatform(),
    entry_point: window.location.pathname,

    referral_source: classifyReferrer(document.referrer),
    utm_campaign: params.get('utm_campaign'),
    utm_medium: params.get('utm_medium'),
    utm_source: params.get('utm_source'),

    device_region: navigator.language,
    device_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    browser_name: browser.name,
    browser_version: browser.version,
    screen_resolution: `${window.innerWidth}x${window.innerHeight}`,
    app_language:
      document.documentElement.lang || navigator.language.split('-')[0],

    screens_visited: [window.location.pathname],

    tour_id: null,
    tour_started: false,

    // Backend uses event TYPE to determine scoring:
    //   stops_viewed      → implicit tag scoring
    //   audio_plays       → implicit tag scoring (+ bonus if >75%)
    //   scroll_depths     → implicit bonus (if depth >50%)
    //   external_link_taps → implicit tag scoring
    //   favorites_added   → explicit tag scoring
    //   filters_confirmed → explicit tag scoring
    //   search_queries    → explicit (backend matches against tag vocabulary)
    marker_clicks: [],
    stops_viewed: [],
    audio_plays: [],
    external_link_taps: [],
    search_queries: [],
    filters_confirmed: [],
    favorites_added: [],
    photo_galleries_opened: 0,
    scroll_depths: [],

    features_used: [],

    tab_visible_seconds: 0,
    tab_switches: 0,

    tour_start_page_opened_at: null,
    tour_start_page_closed_at: null,
    chat_opened_at: null,
    chat_closed_at: null,
    navigation_mode: null,
  };
}

type DebugEvent = { type: string; data: any; timestamp: number };
type DebugListener = () => void;

class PassiveDataCollector {
  private session: RawSessionData = createFreshSession();
  private flushed = false;
  private lastVisibleStart = Date.now();

  // Debug support
  private _debugLog: DebugEvent[] = [];
  private _debugListeners: Set<DebugListener> = new Set();

  private boundHandleUnload: () => void;
  private boundHandleVisibility: () => void;

  constructor() {
    this.boundHandleUnload = () => this.endSession();
    this.boundHandleVisibility = () => {
      if (document.hidden) {
        this.session.tab_visible_seconds +=
          (Date.now() - this.lastVisibleStart) / 1000;
        this.session.tab_switches++;
      } else {
        this.lastVisibleStart = Date.now();
      }
    };
  }

  // ─── Session Lifecycle ───────────────────────────────

  startSession(_userId: string) {
    this.flushed = false;
    this.session = createFreshSession();
    this.lastVisibleStart = Date.now();

    window.addEventListener('beforeunload', this.boundHandleUnload);
    document.addEventListener('visibilitychange', this.boundHandleVisibility);

    // Firebase Analytics: session_start
    AnalyticsService.logSessionStart(
      this.session.platform,
      this.session.device_region,
      this.session.entry_point,
    );
  }

  endSession() {
    if (this.flushed) return;
    this.flushed = true;

    // Finalize tab visible time
    if (!document.hidden) {
      this.session.tab_visible_seconds +=
        (Date.now() - this.lastVisibleStart) / 1000;
    }
    this.session.tab_visible_seconds = Math.round(
      this.session.tab_visible_seconds
    );

    this.session.session_end = Date.now();

    // Firebase Analytics: session_end
    const durationSeconds = Math.round(
      (this.session.session_end - this.session.session_start) / 1000
    );
    AnalyticsService.logSessionEnd(
      this.session.platform,
      durationSeconds,
      this.session.tour_id,
    );

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.session));
      console.log('[PassiveDataCollector] Session saved', this.session);
    } catch (e) {
      console.error('[PassiveDataCollector] Failed to save:', e);
    }

    window.removeEventListener('beforeunload', this.boundHandleUnload);
    document.removeEventListener('visibilitychange', this.boundHandleVisibility);
  }

  // ─── Implicit tag events (stop engagement) ──────────

  /** User clicked a stop marker on the map */
  trackMarkerClick(stop: Stop) {
    const event: MarkerClickEvent = {
      stop_id: stop.id,
      stop_tags: extractTags(stop),
      stop_type: (stop as any).type || 'unknown',
      timestamp: Date.now(),
    };
    this.session.marker_clicks.push(event);

    // Firebase Analytics: stop_marker_click
    AnalyticsService.logStopView(
      this.session.tour_id,
      stop.id,
      event.stop_tags.join(','),
      'marker_click',
    );

    this.emitDebug('marker_click', event);
  }

  /** User opened stop detail — implicit signal per stop tag */
  trackStopViewed(stop: Stop, wasInRadius = false) {
    const event: StopViewEvent = {
      stop_id: stop.id,
      stop_tags: extractTags(stop),
      timestamp: Date.now(),
      closed_at: null,
      was_in_radius: wasInRadius,
      close_method: null,
    };
    this.session.stops_viewed.push(event);

    // Firebase Analytics: stop_view
    const stopType = (stop as any).s_type || 'default';
    AnalyticsService.logStopView(
      this.session.tour_id,
      stop.id,
      event.stop_tags.join(','),
      stopType,
    );

    // GCF: profile event
    dispatchProfileEvent('trackStopViewed', {
      stopId: stop.id,
      tags: event.stop_tags,
      was_in_radius: wasInRadius,
    });

    this.emitDebug('stop_viewed', event);
  }

  /** User closed stop detail — fills closed_at on the last view event for this stop */
  trackStopClosed(stopId: string, wasInRadius = false, closeMethod: 'visited' | 'closed' = 'closed') {
    for (let i = this.session.stops_viewed.length - 1; i >= 0; i--) {
      const event = this.session.stops_viewed[i];
      if (event.stop_id === stopId && event.closed_at === null) {
        event.closed_at = Date.now();
        event.was_in_radius = wasInRadius;
        event.close_method = closeMethod;
        const dwellMs = event.closed_at - event.timestamp;

        // Firebase Analytics: stop_close
        AnalyticsService.logStopClose(
          this.session.tour_id,
          stopId,
          Math.round(dwellMs / 1000),
        );

        // GCF: profile event
        dispatchProfileEvent('trackStopClosed', {
          stopId,
          dwell_ms: dwellMs,
          was_in_radius: wasInRadius,
          close_method: closeMethod,
        });

        this.emitDebug('stop_closed', { stop_id: stopId, closed_at: event.closed_at, dwell_ms: dwellMs, was_in_radius: wasInRadius, close_method: closeMethod });
        break;
      }
    }
  }

  /** User played audio on a stop — implicit signal per stop tag */
  trackAudioPlay(stop: Stop, percentListened: number) {
    const event: AudioPlayEvent = {
      stop_id: stop.id,
      stop_tags: extractTags(stop),
      percent_listened: percentListened,
      timestamp: Date.now(),
    };
    this.session.audio_plays.push(event);
    this.addFeatureUsed('audio_guide');

    // Firebase Analytics: audio_complete
    AnalyticsService.logAudioComplete(
      this.session.tour_id,
      stop.id,
      event.stop_tags.join(','),
      percentListened,
    );

    this.emitDebug('audio_play', event);
  }

  /** User scrolled stop detail — implicit bonus signal per stop tag */
  trackScrollDepth(stop: Stop, depth: number) {
    const event: ScrollDepthEvent = {
      stop_id: stop.id,
      stop_tags: extractTags(stop),
      max_depth: depth,
      timestamp: Date.now(),
    };
    this.session.scroll_depths.push(event);

    // Firebase Analytics: scroll_depth
    AnalyticsService.logScrollDepth(
      this.session.tour_id,
      stop.id,
      event.stop_tags.join(','),
      depth,
    );

    this.emitDebug('scroll_depth', event);
  }

  /** User tapped external link from stop — implicit signal per stop tag */
  trackExternalLink(stop: Stop, url: string) {
    const event: ExternalLinkEvent = {
      stop_id: stop.id,
      stop_tags: extractTags(stop),
      url,
      timestamp: Date.now(),
    };
    this.session.external_link_taps.push(event);

    // Firebase Analytics: external_link_tap
    let linkDomain = 'unknown';
    try { linkDomain = new URL(url).hostname; } catch {}
    AnalyticsService.logExternalLinkTap(
      this.session.tour_id,
      stop.id,
      event.stop_tags.join(','),
      linkDomain,
    );

    this.emitDebug('external_link', event);
  }

  trackPhotoGallery() {
    this.session.photo_galleries_opened++;
    this.emitDebug('photo_gallery', { count: this.session.photo_galleries_opened });
  }

  // ─── Explicit tag events (user actively chose) ──────

  /** User bookmarked a stop — explicit signal per stop tag */
  trackFavoriteAdded(stop: Stop) {
    const event: FavoriteEvent = {
      stop_id: stop.id,
      stop_tags: extractTags(stop),
      timestamp: Date.now(),
    };
    this.session.favorites_added.push(event);
    this.addFeatureUsed('favorites');

    // Firebase Analytics: favorite_toggle (add)
    AnalyticsService.logFavoriteToggle(
      this.session.tour_id,
      stop.id,
      event.stop_tags.join(','),
      'add',
    );

    // GCF: profile event
    dispatchProfileEvent('trackFavoriteAdded', {
      stopId: stop.id,
      tags: event.stop_tags,
    });

    this.emitDebug('favorite_added', event);
  }

  /** User confirmed filter selection — explicit signal per selected tag */
  trackFilterApplied(tag: string) {
    if (!this.session.filters_confirmed.includes(tag)) {
      this.session.filters_confirmed.push(tag);
    }
    this.addFeatureUsed('filters');

    // Firebase Analytics: filter_show_results
    AnalyticsService.logFilterShowResults(
      this.session.tour_id,
      this.session.filters_confirmed.join(','),
    );

    // GCF: profile event
    dispatchProfileEvent('trackFilterApplied', {
      selected_tags: this.session.filters_confirmed,
    });

    this.emitDebug('filter_applied', { tag });
  }

  /** User searched — explicit signal (backend matches query to tags) */
  trackSearch(query: string) {
    if (this.session.search_queries.length < 10) {
      this.session.search_queries.push(query);
    }
    this.addFeatureUsed('search');

    // Firebase Analytics: search_performed
    AnalyticsService.logSearchPerformed(this.session.tour_id, query);

    // GCF: profile event
    dispatchProfileEvent('trackSearch', {
      query,
    });

    this.emitDebug('search', { query });
  }

  // ─── Non-tag tracking ───────────────────────────────

  trackScreenVisited(name: string) {
    this.session.screens_visited.push(name);
  }

  trackTourStarted(tourId: string, totalStops?: number) {
    this.session.tour_id = tourId;
    this.session.tour_started = true;

    // Firebase Analytics: tour_start
    AnalyticsService.logTourStart(tourId, totalStops || 0);

    // GCF: profile event
    dispatchProfileEvent('trackTourStarted', {
      platform: this.session.platform,
    });

    this.emitDebug('tour_started', { tourId });
  }

  trackNavigationUsed(mode: string) {
    this.session.navigation_mode = mode;
    this.addFeatureUsed('map_navigation');

    // Firebase Analytics: navigation_used
    AnalyticsService.logNavigationUsed(this.session.tour_id, mode);

    this.emitDebug('navigation_used', { mode });
  }

  trackOfflineDownload() {
    this.addFeatureUsed('offline_download');

    // Firebase Analytics: offline_download
    AnalyticsService.logOfflineDownload(this.session.tour_id);

    this.emitDebug('offline_download', {});
  }

  trackChatOpened() {
    this.addFeatureUsed('chat');

    // Firebase Analytics: chat_open
    AnalyticsService.logChatOpen(this.session.tour_id);

    this.emitDebug('chat_opened', {});
  }

  trackShareUsed() {
    this.addFeatureUsed('share');
    this.emitDebug('share_used', {});
  }

  trackTourStartPageOpened() {
    this.session.tour_start_page_opened_at = Date.now();
    this.emitDebug('tour_start_page_opened', {});
  }

  trackTourStartPageClosed() {
    if (this.session.tour_start_page_opened_at) {
      this.session.tour_start_page_closed_at = Date.now();

      // Firebase Analytics: tour_start_page_duration
      const durationSeconds = Math.round((this.session.tour_start_page_closed_at - this.session.tour_start_page_opened_at) / 1000);
      AnalyticsService.logTourStartPageDuration(
        this.session.tour_id,
        durationSeconds,
      );

      this.emitDebug('tour_start_page_closed', { closed_at: this.session.tour_start_page_closed_at });
    }
  }

  trackChatScreenOpened() {
    this.session.chat_opened_at = Date.now();
    this.emitDebug('chat_screen_opened', {});
  }

  trackChatScreenClosed() {
    if (this.session.chat_opened_at) {
      this.session.chat_closed_at = Date.now();
      const chatDurationMs = this.session.chat_closed_at - this.session.chat_opened_at;

      // Firebase Analytics: chat_close (with duration)
      AnalyticsService.logChatClose(
        this.session.tour_id,
        Math.round(chatDurationMs / 1000),
      );

      this.emitDebug('chat_screen_closed', { chat_duration_ms: chatDurationMs });
    }
  }

  // ─── Helpers ────────────────────────────────────────

  private addFeatureUsed(feature: string) {
    if (!this.session.features_used.includes(feature)) {
      this.session.features_used.push(feature);

      // Firebase Analytics: feature_first_use (first time this session)
      AnalyticsService.logFeatureFirstUse(feature, this.session.tour_id);
    }
  }

  private emitDebug(type: string, data: any) {
    this._debugLog.push({ type, data, timestamp: Date.now() });
    this._debugListeners.forEach((fn) => fn());
  }

  // ─── Debug API ─────────────────────────────────────

  getSessionData(): RawSessionData {
    return { ...this.session };
  }

  getDebugLog(): DebugEvent[] {
    return this._debugLog;
  }

  onDebugUpdate(fn: DebugListener): () => void {
    this._debugListeners.add(fn);
    return () => this._debugListeners.delete(fn);
  }
}

export const passiveCollector = new PassiveDataCollector();
